import { useQuery } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { useMemo, useState } from "react";

import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "chart.js/auto";
import zoomPlugin from "chartjs-plugin-zoom";
import "chartjs-adapter-moment";

import { useDateRangeStore } from "./date-range-picker/use-date-range-store";
import { convertUTCtoIST } from "@/utils";
import {
  getAllAssignedLeadLogsByDateQueryOptions,
  getAllEmployeesForSAQueryOptions,
} from "@/config/queries/superadmin-query-options";
import { DateRangePicker } from "./date-range-picker/date-range-picker2";
import { ScrollArea } from "@/components/ui/scroll-area/scroll-area";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover/popover";
import { Checkbox } from "@/components/ui/checkbox/checkbox";
import { Button } from "@/components/ui/button/button";
import { Label } from "@/components/ui/label/label";

// Register the zoom plugin
ChartJS.register(zoomPlugin);

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export const LeadsAssignedToEmployeeChart = () => {
  const user = Cookies.get("key") as string;

  const { data, isPending, isFetching } = useQuery(
    getAllEmployeesForSAQueryOptions(user)
  );

  const [selectedEmps, setSelectedEmps] = useState<string[]>([]);

  const employeesData = useMemo(() => {
    if (isPending || isFetching) {
      return [];
    }

    if (data === undefined || data?.employees === undefined) {
      return [];
    }

    const result = data?.employees?.map((emp) => {
      return {
        id: emp?._id,
        name: emp?.name,
      };
    });

    return result;
  }, [data, isPending, isFetching]);

  const { dateRange } = useDateRangeStore();

  const startdate = dateRange?.from as Date;
  const enddate = dateRange?.to as Date;

  const {
    data: allLeadLogsResponse,
    isPending: isLeadLogsPending,
    isFetching: isLeadLogsFetching,
  } = useQuery(
    getAllAssignedLeadLogsByDateQueryOptions(
      user,
      convertUTCtoIST(new Date(startdate.setHours(0))),
      convertUTCtoIST(new Date(enddate.setHours(23)))
    )
  );

  const completeChartData = useMemo(() => {
    const labels = employeesData?.map((emp) => emp?.id);
    // let datasets = employeesData?.map((emp) => {
    //   return {
    //     id: emp?.id,
    //     label: emp?.name,
    //     data: Array(labels.length).fill(0),
    //   };
    // });

    const datasets: {
      label: string;
      data: number[];
      borderWidth: number;
      barThickness: number;
    }[] = [
      {
        label: "Lead Assigned",
        data: Array(labels.length).fill(0),
        borderWidth: 1,
        barThickness: 40,
      },
    ];

    if (isLeadLogsPending) {
      return {
        labels: employeesData?.map((emp) => emp?.name),
        datasets,
      };
    }

    if (
      allLeadLogsResponse === undefined ||
      allLeadLogsResponse.logs === undefined
    ) {
      return {
        labels: employeesData?.map((emp) => emp?.name),
        datasets,
      };
    }

    const filteredEmpIndexes = selectedEmps?.map((id) => {
      const findIndex = employeesData?.findIndex((emp) => emp.id === id);

      if (findIndex !== -1) {
        return findIndex;
      }
    });

    const leadLogs = allLeadLogsResponse?.logs;

    for (const leadLog of leadLogs) {
      const findEmp = labels.findIndex((it) => it === leadLog?.createdBy?._id);

      const prevData = datasets?.[0]?.data;

      let newData = prevData?.map((d, index) => {
        if (index === findEmp) {
          return d + 1;
        }

        return d;
      });

      datasets[0].data = newData;
    }

    let latestLabels = employeesData;
    let latestDatas = datasets[0]?.data;

    if (filteredEmpIndexes.length > 0) {
      latestLabels = latestLabels.filter((_, index) => {
        if (filteredEmpIndexes.includes(index)) {
          return true;
        }

        return false;
      });

      latestDatas = latestDatas.filter((_, index) => {
        if (filteredEmpIndexes.includes(index)) {
          return true;
        }

        return false;
      });
    }

    datasets[0].data = latestDatas;

    return {
      labels: latestLabels?.map((emp) => emp?.name),
      datasets,
    };
  }, [
    employeesData,
    allLeadLogsResponse,
    isLeadLogsFetching,
    isLeadLogsPending,
    selectedEmps,
  ]);

  return (
    <div className="">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-medium">
          Report 1: Number of Leads assigned to employees
        </h1>
        <DateRangePicker />
      </div>

      <div className="flex items-center justify-end">
        <Popover>
          <PopoverTrigger>
            <Button size="sm" variant="outline" className="my-3">
              {selectedEmps?.length > 0 ? (
                <div>{selectedEmps?.length} Employee Selected</div>
              ) : (
                <div>Select Employees</div>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="mr-5">
            <ScrollArea className="max-h-[200px] overflow-y-auto">
              <div className="flex flex-col gap-3 max-h-[200px] overflow-y-auto">
                {employeesData?.map((emp, index) => {
                  return (
                    <div key={index} className="flex items-center gap-1">
                      <Checkbox
                        id={`emp-${index}`}
                        checked={selectedEmps.includes(emp?.id)}
                        onCheckedChange={(checked) => {
                          return checked
                            ? setSelectedEmps((prev) => [...prev, emp.id])
                            : setSelectedEmps(
                                selectedEmps?.filter(
                                  (empId) => empId !== emp.id
                                )
                              );
                        }}
                      />
                      <Label htmlFor={`emp-${index}`}>{emp?.name}</Label>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </PopoverContent>
        </Popover>
      </div>

      <div className="h-[70vh]">
        <Bar
          data={completeChartData}
          options={{
            scales: {
              y: {
                min: 0,
              },
            },
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              zoom: {
                pan: {
                  enabled: true,
                  mode: "x",
                },
                zoom: {
                  wheel: {
                    enabled: true,
                  },
                  pinch: {
                    enabled: true,
                  },
                  mode: "x",
                },
              },
            },
          }}
        />
      </div>
    </div>
  );
};
